import {
  AdControlNativeModel,
  AdControlRequest,
  AdropParamsFactory,
  useAdControlNative,
} from '@/entities/advertisement';
import useAdBanner from '@/hooks/advertisement/useAdBanner';
import useGetBannerAdExchange from '@/hooks/advertisement/useGetBannerAdExchange';
import { AdPageType, BannerAdExchangeModel } from '@/models/ad/list';
import { getAuthToken } from '@/recoil/atoms/common/auth';
import { parsePayload } from '@/shared/utils';
import { Client } from '@/types/app';
import { QueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';

export enum UnifiedAdNativeType {
  AdControl = 'adControl',
  AdExchange = 'adExchange',
  None = 'none',
}

export type SelectedAdNativeData =
  | {
      type: UnifiedAdNativeType.AdControl;
      data: AdControlNativeModel;
    }
  | { type: UnifiedAdNativeType.AdExchange; data: BannerAdExchangeModel }
  | { type: UnifiedAdNativeType.None };

const useUnifiedAdNative = (
  adPageType: AdPageType,
  client: Client,
  enabled = true,
  contextId?: string,
  locale?: string
) => {
  const router = useRouter();
  const ref = useRef<string>();
  const token = getAuthToken();

  // AdControl 관련 설정
  const adropParamsFactory = useMemo(() => {
    return new AdropParamsFactory(adPageType, client);
  }, [adPageType, client]);

  const userId = token ? `${parsePayload(token).id}` : undefined;

  const adControlRequest: AdControlRequest = {
    unitId: adropParamsFactory.getUnitId()!,
    userId: userId,
    platform: adropParamsFactory.getPlatform(),
    locale: locale || 'ko_KR',
    contextId: contextId,
    trackMode: 1,
  };

  // 병렬로 두 광고 소스 조회
  const {
    adModel: adControlData,
    isLoading: isAdControlLoading,
    refetch: refetchAdControl,
  } = useAdControlNative(adControlRequest, {
    enabled: enabled && !!adControlRequest.unitId,
  });

  const { bannerAdExchange, handleSendExposureLog, isAdExchangeLoading, refetchBannerAdExchange } = useAdBanner(
    adPageType,
    client,
    enabled
  );

  // 랜덤 선택 로직
  const selectRandomAd = useMemo((): SelectedAdNativeData => {
    const availableAds: (
      | { type: UnifiedAdNativeType.AdControl; data: AdControlNativeModel }
      | { type: UnifiedAdNativeType.AdExchange; data: BannerAdExchangeModel }
    )[] = [];

    if (adControlData?.isVisible) {
      availableAds.push({ type: UnifiedAdNativeType.AdControl, data: adControlData });
    }
    if (bannerAdExchange) {
      availableAds.push({ type: UnifiedAdNativeType.AdExchange, data: bannerAdExchange });
    }

    if (availableAds.length === 0) {
      return { type: UnifiedAdNativeType.None };
    }

    if (availableAds.length === 1) {
      return availableAds[0];
    }

    // 50:50 랜덤 선택
    const randomIndex = Math.floor(Math.random() * availableAds.length);
    return availableAds[randomIndex];
  }, [adControlData, bannerAdExchange]);

  // 페이지 변경 시 refetch
  useEffect(() => {
    if (ref.current !== undefined && ref.current !== router.pathname) {
      Promise.allSettled([refetchAdControl(), refetchBannerAdExchange()]);
    }
    ref.current = router.pathname;
  }, [router.pathname]);

  const isLoading = isAdControlLoading || isAdExchangeLoading;

  return {
    selectedAd: selectRandomAd,
    isLoading,
    adropParamsFactory,
    handleAdExchangeExposureLog: handleSendExposureLog,
  };
};

export default useUnifiedAdNative;

useUnifiedAdNative.prefetchQuery = async (
  queryClient: QueryClient,
  adPageType: AdPageType,
  client: Client,
  contextId?: string
) => {
  await Promise.allSettled([
    useAdControlNative.prefetchQuery(queryClient, adPageType, client, contextId),
    useGetBannerAdExchange.prefetchQuery(queryClient, adPageType, client),
  ]);
};
